import { useCallback, useEffect, useRef, useState } from "react";

import { detectCodec } from "./codecs";
import { type CapturedMedia, generateFilename } from "./utils";

// Mapped from the native MediaRecorder error's DOMException name.
export type RecordingErrorKind =
  | "unsupported"
  | "size_exceeded"
  | "duration_exceeded"
  | "invalid_state" // InvalidStateError
  | "security" // SecurityError
  | "track_changed" // InvalidModificationError
  | "encoding_failed" // EncodingError
  | "unknown"; // UnknownError, or an unrecognised DOMException

export interface RecordingError {
  kind: RecordingErrorKind;
  message: string;
  cause?: unknown;
  /** Raw DOMException name behind `kind`, kept for telemetry detail. */
  domErrorName?: string;
}

const MEDIA_RECORDER_ERROR_KIND_BY_DOM_NAME: Readonly<
  Record<string, RecordingErrorKind>
> = {
  InvalidStateError: "invalid_state",
  SecurityError: "security",
  InvalidModificationError: "track_changed",
  EncodingError: "encoding_failed",
};

function mapMediaRecorderErrorKind(
  domErrorName: string | undefined
): RecordingErrorKind {
  if (domErrorName == null) return "unknown";
  return MEDIA_RECORDER_ERROR_KIND_BY_DOM_NAME[domErrorName] ?? "unknown";
}

export interface UseMediaRecorderOptions {
  stream: MediaStream | null;
  /** Determines codec list and output filename prefix. */
  kind: "audio" | "video";
  bitrate?: number;
  /**
   * Interval in milliseconds at which `ondataavailable` fires.
   * @default 1000
   */
  timeslice?: number;
  maxFileSize: number;
  maxDurationSeconds: number;
  /**
   * Flatten MP4 captures into a progressive container before handing them to
   * the upload path (see `remux.ts`).
   */
  remux?: boolean;
  /**
   * Milliseconds to wait for a remux before falling back to the raw blob.
   * @default 30000
   */
  remuxTimeoutMs?: number;
  onRecordingStarted?: () => void;
  onMediaCaptured?: (media: CapturedMedia, duration: number) => void;
  onRecordingError?: (error: RecordingError) => void;
  /** Fired just before a remux begins, with the raw capture size. */
  onRemuxStart?: (info: { inputBytes: number }) => void;
  /** Fired when a remux is skipped via fallback, so the caller can log it. */
  onRemuxFallback?: (error: unknown) => void;
}

export interface UseMediaRecorderReturn {
  startRecording: () => void;
  stopRecording: () => void;
  isRecording: boolean;
  recordingDuration: number;
  /** True while a capture is being remuxed, between stop and upload. */
  isProcessing: boolean;
}

const DEFAULT_REMUX_TIMEOUT_MS = 30_000;

/** Remux applies only to MP4 containers; WebM and others pass through. */
function isRemuxableMp4(mimeType: string): boolean {
  return mimeType.startsWith("video/mp4") || mimeType.startsWith("audio/mp4");
}

function teardownRecorder(recorder: MediaRecorder): void {
  recorder.ondataavailable = null;
  recorder.onstop = null;
  recorder.onerror = null;
}

/**
 * Low-level hook that wraps the `MediaRecorder` API for a given stream.
 *
 * Knows nothing about camera management or photo capture. Errors surface via
 * `onRecordingError` — the hook never calls `alert()`. The duration timer uses
 * a ref-based stop callback to avoid stale closures.
 *
 * A recording binds to the track instances present when `startRecording` calls
 * `recorder.start()`.
 */
export function useMediaRecorder({
  stream,
  kind,
  bitrate,
  timeslice = 1000,
  maxFileSize,
  maxDurationSeconds,
  remux = true,
  remuxTimeoutMs = DEFAULT_REMUX_TIMEOUT_MS,
  onRecordingStarted,
  onMediaCaptured,
  onRecordingError,
  onRemuxStart,
  onRemuxFallback,
}: UseMediaRecorderOptions): UseMediaRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recordingDurationRef = useRef(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  /** Guards against starting a new recording while onstop is still pending. */
  const isFinalizingRef = useRef(false);

  // Always-fresh refs so internal callbacks never capture stale values.
  const callbacksRef = useRef({
    onRecordingStarted,
    onMediaCaptured,
    onRecordingError,
    onRemuxStart,
    onRemuxFallback,
  });
  useEffect(() => {
    callbacksRef.current = {
      onRecordingStarted,
      onMediaCaptured,
      onRecordingError,
      onRemuxStart,
      onRemuxFallback,
    };
  });

  const remuxRef = useRef({ enabled: remux, timeoutMs: remuxTimeoutMs });
  useEffect(() => {
    remuxRef.current = { enabled: remux, timeoutMs: remuxTimeoutMs };
  }, [remux, remuxTimeoutMs]);

  const maxDurationRef = useRef(maxDurationSeconds);
  useEffect(() => {
    maxDurationRef.current = maxDurationSeconds;
  }, [maxDurationSeconds]);

  // Stable ref to stopRecording so the timer interval can call it without
  // capturing a stale function reference.
  const stopRecordingRef = useRef<() => void>(() => undefined);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current == null ||
      mediaRecorderRef.current.state !== "recording"
    ) {
      return;
    }

    mediaRecorderRef.current.stop();
    isFinalizingRef.current = true;
    // isRecording flag and timer are cleared in onstop so all stop paths
    // (manual, size-exceeded, error) converge on the same teardown.
  }, []);

  // Keep the ref current so the timer closure always calls the latest version.
  stopRecordingRef.current = stopRecording;

  const startRecording = useCallback(() => {
    if (stream == null) return;

    if (isFinalizingRef.current) {
      console.error(
        "Cannot start a new recording while the previous one is finalising."
      );
      return;
    }

    // Tear down any existing recorder that is still active.
    if (
      mediaRecorderRef.current != null &&
      mediaRecorderRef.current.state === "recording"
    ) {
      teardownRecorder(mediaRecorderRef.current);
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    const codec = detectCodec(kind);
    if (codec == null) {
      callbacksRef.current.onRecordingError?.({
        kind: "unsupported",
        message: `${kind === "audio" ? "Audio" : "Video"} recording is not supported in your browser.`,
      });
      return;
    }

    chunksRef.current = [];
    let accumulatedSize = 0;

    try {
      const recorderOptions: MediaRecorderOptions = {
        mimeType: codec.mimeType,
      };
      if (bitrate != null) {
        if (kind === "audio") {
          recorderOptions.audioBitsPerSecond = bitrate;
        } else {
          recorderOptions.videoBitsPerSecond = bitrate;
        }
      }

      const recorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size === 0) return;

        accumulatedSize += event.data.size;
        chunksRef.current.push(event.data);

        // Stop as soon as we exceed the limit so the recorder doesn't keep
        // buffering. onstop will see blob.size > maxFileSize and fire the
        // error there
        if (
          accumulatedSize > maxFileSize &&
          mediaRecorderRef.current?.state === "recording"
        ) {
          mediaRecorderRef.current.stop();
          isFinalizingRef.current = true;
        }
      };

      recorder.onstop = () => {
        // Always reset live-recording state — applies to manual stop,
        // size-exceeded self-stop, and duration-cap stop alike.
        setIsRecording(false);
        if (timerRef.current != null) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        const blob = new Blob(chunksRef.current, { type: codec.mimeType });
        chunksRef.current = [];

        if (blob.size > maxFileSize) {
          callbacksRef.current.onRecordingError?.({
            kind: "size_exceeded",
            message: `Recording stopped: maximum file size of ${Math.round(maxFileSize / 1024 / 1024)} MB reached.`,
          });
          if (mediaRecorderRef.current != null) {
            teardownRecorder(mediaRecorderRef.current);
            mediaRecorderRef.current = null;
          }
          isFinalizingRef.current = false;
          return;
        }

        const emitCaptured = (output: Blob) => {
          const prefix = kind === "audio" ? "recording-audio" : "recording";
          const file = new File(
            [output],
            generateFilename(prefix, codec.extension),
            { type: codec.mimeType }
          );
          const url = URL.createObjectURL(output);

          callbacksRef.current.onMediaCaptured?.(
            { type: kind, url, file },
            recordingDurationRef.current
          );

          // Defer teardown so this onstop handler completes before the ref
          // is cleared — matches the existing `CLEANUP_DELAY_MS = 0` pattern.
          // isFinalizingRef stays set across the remux so a new recording
          // can't start mid-flatten.
          setTimeout(() => {
            if (mediaRecorderRef.current != null) {
              teardownRecorder(mediaRecorderRef.current);
              mediaRecorderRef.current = null;
            }
            isFinalizingRef.current = false;
          }, 0);
        };

        if (!remuxRef.current.enabled || !isRemuxableMp4(codec.mimeType)) {
          emitCaptured(blob);
          return;
        }

        // Remux the MP4 into a progressive container before handing it to the
        // upload path. Best-effort: if remux fails or times out, fall back to
        // the raw blob.
        setIsProcessing(true);
        callbacksRef.current.onRemuxStart?.({ inputBytes: blob.size });
        let settled = false;
        function settle(output: Blob, fallbackError?: unknown) {
          if (settled) return;
          settled = true;
          setIsProcessing(false);
          if (fallbackError !== undefined) {
            callbacksRef.current.onRemuxFallback?.(fallbackError);
          }
          emitCaptured(output);
        }

        // Aborting on timeout cancels the in-flight conversion so it stops
        // burning CPU/memory once we've already fallen back to the raw blob.
        const abortController = new AbortController();
        const timeout = setTimeout(() => {
          abortController.abort();
          settle(blob, new Error("Remux timed out"));
        }, remuxRef.current.timeoutMs);
        import("./remux")
          .then(({ remuxToProgressiveMp4 }) =>
            remuxToProgressiveMp4(blob, { signal: abortController.signal })
          )
          .then((progressive) => {
            clearTimeout(timeout);
            settle(progressive);
          })
          .catch((error: unknown) => {
            clearTimeout(timeout);
            settle(blob, error);
          });
      };

      recorder.onerror = (event) => {
        console.error("MediaRecorder error", event);
        // Native event carries a DOMException on `.error` (per spec/MDN);
        // lib.dom.d.ts still types this as plain ErrorEvent, hence the cast.
        const domErrorName = (event as { error?: { name?: string } }).error
          ?.name;
        callbacksRef.current.onRecordingError?.({
          kind: mapMediaRecorderErrorKind(domErrorName),
          message: "An error occurred during recording.",
          cause: event,
          domErrorName,
        });
        setIsRecording(false);
        if (timerRef.current != null) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        if (mediaRecorderRef.current != null) {
          teardownRecorder(mediaRecorderRef.current);
          mediaRecorderRef.current = null;
        }
        isFinalizingRef.current = false;
      };

      recorder.start(timeslice);
      setIsRecording(true);
      recordingDurationRef.current = 0;
      setRecordingDuration(0);

      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => {
          const next = prev + 1;
          recordingDurationRef.current = next;
          if (next >= maxDurationRef.current) {
            stopRecordingRef.current();
          }
          return next;
        });
      }, 1000);

      callbacksRef.current.onRecordingStarted?.();
    } catch (error) {
      console.error("Failed to start MediaRecorder", error);
      // Per spec, start() throws InvalidStateError synchronously when the
      // recorder isn't inactive — that lands here, not in onerror.
      // https://www.w3.org/TR/mediastream-recording/#dom-mediarecorder-start (15.5)
      const domErrorName =
        error instanceof DOMException ? error.name : undefined;
      callbacksRef.current.onRecordingError?.({
        kind: mapMediaRecorderErrorKind(domErrorName),
        message: `Failed to start recording. Your browser may not support ${kind} recording.`,
        cause: error,
        domErrorName,
      });
    }
  }, [stream, kind, bitrate, timeslice, maxFileSize]);

  // Cleanup on unmount — stop any active recorder silently.
  useEffect(() => {
    return () => {
      if (timerRef.current != null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (mediaRecorderRef.current != null) {
        teardownRecorder(mediaRecorderRef.current);
        if (mediaRecorderRef.current.state !== "inactive") {
          try {
            mediaRecorderRef.current.stop();
          } catch {
            // already stopped
          }
        }
        mediaRecorderRef.current = null;
      }
      chunksRef.current = [];
    };
  }, []);

  return {
    startRecording,
    stopRecording,
    isRecording,
    recordingDuration,
    isProcessing,
  };
}
